import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { bundleSavings } from "../mock/mock";

const CartContext = createContext(null);
const STORAGE_KEY = "anaa_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error("Cart: failed to read saved cart", e);
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error("Cart: failed to save cart", e);
    }
  }, [items]);

  const addItem = (product, size, qty = 1) => {
    const key = `${product.slug}__${size}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name: product.name,
          brand: product.brand,
          image: product.image,
          price: product.sale || product.price,
          size,
          qty,
        },
      ];
    });
    setIsOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const updateQty = (key, qty) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i))
        .filter((i) => i.qty > 0)
    );
  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((n, i) => n + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((n, i) => n + i.price * i.qty, 0), [items]);
  const discount = useMemo(() => Math.min(bundleSavings(items), subtotal), [items, subtotal]);
  const total = useMemo(() => Math.max(0, subtotal - discount), [subtotal, discount]);

  const value = { items, addItem, removeItem, updateQty, clear, count, subtotal, discount, total, isOpen, setIsOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
